import { Injectable } from '@angular/core';
import { UserInfoService } from '../auth-service/user-info-service';
import { PermissionUiService } from './permission-ui.service';
import { Events } from '../events/events';

@Injectable({ providedIn: 'root' })
export class PermissionAccessService {
  private cachedPermissions: Set<string> | null = null;
  private permissionsLoadPromise: Promise<Set<string>> | null = null;

  constructor(private readonly permissionUi: PermissionUiService) {
    Events.userUpdated.subscribe(() => {
      this.cachedPermissions = null;
      this.permissionsLoadPromise = null;
    });
  }

  private async getPermissions(): Promise<Set<string>> {
    if (this.cachedPermissions) {
      return this.cachedPermissions;
    }

    if (!this.permissionsLoadPromise) {
      this.permissionsLoadPromise = UserInfoService.getUserInfo().then((userInfo) => {
        const permissionSet = new Set(userInfo?.permissions ?? []);
        this.cachedPermissions = permissionSet;
        return permissionSet;
      });
    }

    return this.permissionsLoadPromise;
  }

  async hasAnyPermission(...permissions: Array<string | undefined | null>): Promise<boolean> {
    const userPermissions = await this.getPermissions();

    for (const permission of permissions) {
      if (!permission) {
        continue;
      }
      if (userPermissions.has(permission)) {
        return true;
      }
    }
    return false;
  }

  async canView(viewPermission: string, ...fallbackPermissions: string[]): Promise<boolean> {
    return this.hasAnyPermission(viewPermission, ...fallbackPermissions);
  }

  async canAct(actionPermission: string, ...fallbackPermissions: string[]): Promise<boolean> {
    return this.hasAnyPermission(actionPermission, ...fallbackPermissions);
  }

  async enforceSectionView(
    sectionId: string,
    viewPermission: string,
    fallbackPermissions: string[] = [],
    deniedMessage?: string
  ): Promise<boolean> {
    const allowed = await this.canView(viewPermission, ...fallbackPermissions);
    if (allowed) {
      this.permissionUi.allow(sectionId);
    } else {
      this.permissionUi.deny(sectionId, deniedMessage);
    }
    return allowed;
  }
}
